import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React from "react";
import { Link, useLocation } from "react-router";
import { MenuItem as MenuItemType } from "../../layout/left-sidebar";
import { SxProps, Tooltip } from "@mui/material";
import { useResponsive } from "../../hooks/use-media-query";
import { useDispatch } from "react-redux";
import { activeConIdSet } from "../../redux/userSlice";

interface MenuItemProps {
  item: MenuItemType;
  openSidebar: boolean;
  endIcon?: React.ReactNode;
  sx?: SxProps;
  textWidth?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  handleCloseSidebar?: () => void;
}

// Helper function to determine if a menu item should be active
const isMenuItemActive = (itemLink: string, pathname: string): boolean => {
  // Exact match
  if (pathname === itemLink) return true;

  // For non-root links, check if pathname starts with the link
  if (itemLink !== "/" && pathname.startsWith(itemLink + "/")) return true;

  // Special case for inbox (root "/")
  if (itemLink === "/") {
    // Only highlight inbox for root or conversation IDs (paths that don't match other menu items)
    const otherRoutes = [
      "/setting",
      "/appointment",
      "/campaign",
      "/cars",
      "/report",
      "/login",
      "/task",
      "/help-center",
      "/support",
      "/review-max",
      "/auto-marketing",
      "/knowledge-base",
      "/Facebook",
      "/Sofia-v2",
    ];
    return (
      pathname === "/" ||
      (pathname.startsWith("/") && !otherRoutes.some((route) => pathname.startsWith(route)))
    );
  }

  return false;
};

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  openSidebar,
  onClick,
  sx,
  endIcon,
  handleCloseSidebar,
  textWidth,
}) => {
  const { pathname } = useLocation();

  const { isSmall } = useResponsive();

  const dispatch = useDispatch();

  return item.link ? (
    <Link
      to={item.link}
      style={{ textDecoration: "none" }}
      onClick={() => {
        if (isSmall && handleCloseSidebar) {
          handleCloseSidebar();
        }
        if (item.link === "/") dispatch(activeConIdSet(undefined));
      }}
    >
      <Tooltip title={item.title} placement="right" disableHoverListener={openSidebar}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexDirection: "row",
            color: "white",
            padding: "8px",
            backgroundColor: isMenuItemActive(item.link, pathname) ? "#2962FF" : "transparent",
            borderRadius: "8px",
            justifyContent: openSidebar ? "flex-start" : "center",
            fontSize: "14px",
            ...sx,
          }}
        >
          {item.icon}

          {openSidebar && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "white",
                  textTransform: "capitalize",
                }}
              >
                {item.title}
              </Typography>
              {openSidebar && item.isBeta && (
                <Box
                  sx={{
                    backgroundColor: "#22BE5C",
                    color: "white",
                    borderRadius: "16px",
                    paddingX: "8px",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  Beta
                </Box>
              )}
              {openSidebar && item.isNew && (
                <Box
                  sx={{
                    backgroundColor: "#22BE5C",
                    color: "white",
                    borderRadius: "16px",
                    paddingX: "8px",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  New
                </Box>
              )}
              {openSidebar && item.comingSoon && (
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#e77f25",
                    borderRadius: "50%",
                    transition:
                      "height 0.2s ease-in-out, width 0.2s ease-in-out 0.2s, border-radius 0.2s ease-in-out 0.2s, padding 0.2s ease-in-out 0.2s",
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    "&:hover": {
                      width: "80px",
                      height: "20px",
                      borderRadius: "16px",
                      paddingX: "8px",
                      "& .coming-soon-text": {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                    "& .coming-soon-text": {
                      position: "absolute",
                      color: "white",
                      fontSize: "11px",
                      fontWeight: "400",
                      whiteSpace: "nowrap",
                      opacity: 0,
                      transform: "translateX(10px)",
                      transition: "opacity 0.2s ease-in-out 0.3s, transform 0.2s ease-in-out 0.3s",
                    },
                  }}
                >
                  <span className="coming-soon-text">Coming Soon</span>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Tooltip>
    </Link>
  ) : (
    <Tooltip title={item.title} placement="right" disableHoverListener={openSidebar}>
      <Box
        onClick={onClick}
        sx={{
          cursor: "pointer",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexDirection: "row",
          color: "white",
          padding: "8px",
          // backgroundColor: isMenuItemActive(item.link || "", pathname) ? "#2962FF" : "transparent",
          borderRadius: "8px",
          justifyContent: openSidebar ? "flex-start" : "center",
          ...sx,
        }}
      >
        {item.icon}
        {openSidebar && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                textTransform: "capitalize",
                maxWidth: textWidth || "168px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.title}
            </Typography>
            {endIcon}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default MenuItem;
